import type { Site } from '@hotjar/site';
import { trackClick } from '@hotjar/webapp/tracking/common';
import { help as trackHelp } from '@hotjar/webapp/tracking/help';
import { trigger } from '@hotjar/webapp/tracking/tools/hotjar';
import {
  getComparePlansUrl,
  getCreateCSMTicketFormUrl,
  getCreateTicketUrl,
  getGettingStartedGuideUrl,
  getKnowledgeBaseUrl,
  getNewBillingPaymentTicketFormUrl,
  getNewTicketHelpPageUrl,
  getPlansAndBillingHelpUrl,
} from '@hotjar/webapp/help/links';
import { extractSiteGroupingProps } from '@hotjar/webapp/tracking/utils';

interface GoToHelp {
  /**
   * @param currentSite The user's current site.
   */
  (currentSite: Site, currentLang: string): void;
}

/**
 * Open a link in a new tab/window.
 * @param url the link url
 * @private
 */
const openExternalLink = (url: string): void => {
  window?.open(url, '_blank');
};

/**
 * Open link to knowledgebase, with tracking.
 */
export const goToKnowledgeBase: GoToHelp = (currentSite, currentLang) => {
  trackHelp('Docs', currentSite);
  trigger('clicked_knowledge_base');
  openExternalLink(getKnowledgeBaseUrl(currentSite, currentLang));
};

/**
 * Open link to create a new support ticket, with tracking.
 */
export const goToCreateNewTicket: GoToHelp = (currentSite, currentLang) => {
  trackHelp('New ticket', currentSite);
  trigger('clicked_new_ticket');
  openExternalLink(getCreateTicketUrl(currentSite, currentLang));
};

/**
 * Open link to plans & billing help.
 */
export const goToPlansAndBillingHelp: GoToHelp = (currentSite, currentLang) => {
  trackHelp('Plans & Billing Help', currentSite);
  openExternalLink(getPlansAndBillingHelpUrl(currentSite, currentLang));
};

/**
 * Open link to new ticket help.
 */
export const goToNewTicketHelp: GoToHelp = (currentSite, currentLang) => {
  trackHelp('New ticket help page', currentSite);
  trigger('clicked_new_ticket_help_page');
  openExternalLink(getNewTicketHelpPageUrl(currentSite, currentLang));
};

/**
 * Open link to compare plans page.
 * @param name The element name that was clicked
 * @param currentSite The user's current site.
 */
export const goToComparePlans = (name: string, currentSite: Site, currentLang: string): void => {
  trackHelp('Compare Plans', currentSite);
  trackClick({
    name,
    uiPosition: 'Plans and Billing Page header',
    groupData: extractSiteGroupingProps(currentSite),
  }).catch();
  openExternalLink(getComparePlansUrl(currentSite, currentLang));
};

/**
 * Open link to the new CSM ticket form.
 */
export const goToCreateCSMTicketForm: GoToHelp = (currentSite, currentLang) => {
  trackHelp('Contact our Customer Success Team', currentSite);
  trigger('clicked_new_ticket');
  openExternalLink(getCreateCSMTicketFormUrl(currentSite, currentLang));
};

/**
 * Open link to the new Billing and Payment ticket form.
 * @param source
 * @param currentSite The user's current site.
 */
export const goToNewBillingPaymentTicketForm = (
  source: string,
  currentSite: Site,
  currentLang: string,
): void => {
  trackHelp(source, currentSite);
  trigger('clicked_new_ticket');
  openExternalLink(getNewBillingPaymentTicketFormUrl(currentSite, currentLang));
};

/**
 * Open link to the getting started guide.
 */
export const goToGettingStartedGuide: GoToHelp = (currentSite, currentLang) => {
  trackHelp('Getting started guide', currentSite);
  trigger('clicked_getting_started_guide');
  openExternalLink(getGettingStartedGuideUrl(currentSite, currentLang));
};

declare global {
  interface Window {
    zE: (functionName: string, args: string) => void;
  }
}

export const openChat = (
  fallbackFunc: (args: Record<string, unknown> | null) => void,
  fallbackParams = null,
): void => {
  const isChatLoaded = document.getElementById('launcher');
  if (isChatLoaded && window.zE) {
    window.zE('webWidget', 'open');
  } else {
    fallbackFunc(fallbackParams);
  }
};
